import { IRole, UserLight } from "../user";
import { IGetDealsData } from "./deal";

export interface DealStage {
  id: number;
  title: string;
  status_id: number;
  order: number;
  deals_count: number;
  deals: DealStageItem[];
  is_system: boolean;
  /** Per-stage last page for flat pagination; always 1 while grouping is active. */
  deals_last_page?: number;
  /**
   * Set by the FE when `deals_group_by` was requested: the stage's group nodes (moved out of `deals`,
   * which is then emptied). Grouping disables pagination, so this is the whole stage.
   */
  groups?: DealStageGroup[];
}

/**
 * A group node as returned by the backend inside a stage's `deals` (pipeline) / row list (deals-list).
 * `children` is polymorphic: subgroup nodes for the non-deepest level, deal leaves at the deepest level.
 * Decide by the grouping depth you requested, not by inspecting items.
 */
export interface DealStageGroup {
  /** Ready-to-display title, e.g. "Real Estate" or "No Vintage". */
  group_title: string;
  /** Distinct deals in this subtree — NOT the sum of children (a deal can sit in several groups). */
  deals_count: number;
  children: Array<DealStageGroup | DealStageItem>;
}

export interface DealStageItem {
  id: number;
  title: string;
  asset_class: string;
  status_id: number;
  shared_with: UserLight[];
  deal_note: DealStageItemNote | null;
  is_shared_deal?: boolean;
  role?: IRole | null;
  // `card_characteristics` come back as extra top-level keys spelled exactly as requested,
  // e.g. deal["Vintage"] = ["2018","2019"] | deal["Contact"] = "a@b.com" | null. Read via getCardChips.
}

export interface DealStageItemNote {
  id: number;
  note: string;
}

/** Type guard: distinguishes a group node from a deal leaf inside `children` / `deals`. */
export const isDealStageGroup = (node: DealStageGroup | DealStageItem): node is DealStageGroup =>
  (node as DealStageGroup).group_title !== undefined;

export type GetDealStagesData<T extends boolean> = Partial<Omit<IGetDealsData, "page">> & {
  with_deals: T;
  page?: number;
  freemium_dashboard?: boolean;
  /** Lowercase dimension keys, max 2, e.g. ["asset_class"] or ["asset_class","currency"]. */
  deals_group_by?: string[];
  /** Characteristic names to attach to each card, e.g. ["Asset Class", "Vintage", "Contact"]. */
  card_characteristics?: string[];
};

/** A Group By dimension: `key` is sent to the API, `label` is shown to the user. */
export interface PipelineDimensionOption {
  key: string;
  label: string;
}

/**
 * Pipeline view config persisted server-side via `/user/settings` (key "pipelineViewConfig") — the endpoint
 * stores an arbitrary JSON blob. Shape mirrors the relevant `DealsQueries` fields for direct hydration.
 */
export interface PipelineViewConfig {
  pipelineGroupBy?: string[];
  pipelineCardCharacteristics?: string[];
  pipelineCardCharacteristicOptions?: string[];
  pipelineHiddenStageIds?: number[];
}

export interface CreateDealStageData {
  title: string;
  status_id: number;
}

export interface UpdateDealStageData extends Partial<CreateDealStageData> {
  id: number;
}

export type ReorderDealStagesData = {
  order: Array<{ id: number; position: number }>;
};
